import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  PlusIcon, 
  TrashIcon, 
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { activationService } from '../services/activationService'

interface ActivationCode {
  code: string;
  isUsed: boolean;
  createdAt: Date;
  usedAt?: Date;
  usedBy?: string;
}

const ActivationCodes: React.FC = () => {
  const [codes, setCodes] = useState<ActivationCode[]>([])
  const [statistics, setStatistics] = useState({ total: 0, used: 0, unused: 0 })
  const [loading, setLoading] = useState(true)
  const [newCode, setNewCode] = useState('')
  const [generateCount, setGenerateCount] = useState(5)
  const [filter, setFilter] = useState<'all' | 'used' | 'unused'>('all')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [allCodes, stats] = await Promise.all([
        activationService.getAllCodes(),
        activationService.getStatistics()
      ])
      setCodes(allCodes)
      setStatistics(stats)
    } catch (error) {
      console.error('Veri yüklenirken hata:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateCodes = async () => {
    try {
      await activationService.generateActivationCodes(generateCount)
      await loadData()
      setGenerateCount(5)
    } catch (error) {
      console.error('Kod üretilirken hata:', error)
    }
  }

  const handleAddCode = async () => {
    if (!newCode.trim()) return
    
    try {
      const result = await activationService.addActivationCode(newCode.trim())
      if (result.success) {
        await loadData()
        setNewCode('')
      } else {
        alert(result.message)
      }
    } catch (error) {
      console.error('Kod eklenirken hata:', error)
    }
  }

  const handleDeleteCode = async (code: string) => {
    if (!confirm('Bu kodu silmek istediğinizden emin misiniz?')) return
    
    try {
      const result = await activationService.removeActivationCode(code)
      if (result.success) {
        await loadData()
      } else {
        alert(result.message)
      }
    } catch (error) {
      console.error('Kod silinirken hata:', error)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Basit bir toast mesajı gösterebiliriz
    alert('Kod panoya kopyalandı!')
  }

  const filteredCodes = codes.filter(code => {
    if (filter === 'used') return code.isUsed
    if (filter === 'unused') return !code.isUsed
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Aktivasyon Kodları Yönetimi
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Tek kullanımlık aktivasyon kodlarını yönetin
            </p>
          </div>
          <button
            onClick={loadData}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span>Yenile</span>
          </button>
        </div>
      </motion.div>

      {/* Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Toplam Kod</p>
              <p className="text-3xl font-bold">{statistics.total}</p>
            </div>
            <KeyIcon className="w-8 h-8 text-blue-100" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Kullanılmış</p>
              <p className="text-3xl font-bold">{statistics.used}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-100" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Kullanılmamış</p>
              <p className="text-3xl font-bold">{statistics.unused}</p>
            </div>
            <XCircleIcon className="w-8 h-8 text-orange-100" />
          </div>
        </div>
      </motion.div>

      {/* Code Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
          Kod Yönetimi
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Generate Codes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Otomatik Kod Üretimi
            </h3>
            <div className="flex space-x-3">
              <input
                type="number"
                min="1"
                max="50"
                value={generateCount}
                onChange={(e) => setGenerateCount(parseInt(e.target.value) || 1)}
                className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleGenerateCodes}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Kod Üret</span>
              </button>
            </div>
          </div>

          {/* Add Manual Code */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Manuel Kod Ekleme
            </h3>
            <div className="flex space-x-3">
              <input
                type="text"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="CUSTOM-CODE-2024"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleAddCode}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Ekle</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex space-x-3 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            Tümü ({statistics.total})
          </button>
          <button
            onClick={() => setFilter('unused')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'unused' 
                ? 'bg-orange-600 text-white' 
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            Kullanılmamış ({statistics.unused})
          </button>
          <button
            onClick={() => setFilter('used')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'used' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            Kullanılmış ({statistics.used})
          </button>
        </div>

        {/* Codes Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                  Kod
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                  Durum
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                  Oluşturulma
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                  Kullanım
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCodes.map((code, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm font-mono">
                        {code.code}
                      </code>
                      <button
                        onClick={() => copyToClipboard(code.code)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      code.isUsed 
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {code.isUsed ? 'Kullanılmış' : 'Aktif'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(code.createdAt).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {code.isUsed ? (
                      <div>
                        <div>{code.usedAt ? new Date(code.usedAt).toLocaleDateString('tr-TR') : '-'}</div>
                        <div className="text-xs">{code.usedBy || '-'}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {!code.isUsed && (
                      <button
                        onClick={() => handleDeleteCode(code.code)}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCodes.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {filter === 'all' ? 'Henüz kod bulunmuyor' : 
             filter === 'used' ? 'Kullanılmış kod bulunmuyor' : 
             'Kullanılmamış kod bulunmuyor'}
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default ActivationCodes